import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks';
import { ComponentConfig, type Config } from '@measured/puck';
import {
  type ColumnsProps,
  type FlexProps,
  PuckRenderPage,
  type ContainerProps,
  type VerticalSpaceProps,
  type SimplePageHeaderProps,
  type BillingFormProps,
  type TextFieldProps,
  type ContactInfoProps,
  fontFamilies,
  type TimerProps,
  type ShippingMethodSelectProps,
  type PaymentProps,
  type SingleStepButtonSectionProps,
  type TermsAndSubProps,
  type UpSellProps,
  type CustomerReviewsProps,
  type OrderDetailsProps,
  type DividerProps,
  type MarginProps,
  type HeaderProps,
  type TextEditorProps,
  type ButtonSectionProps,
  type ImageUploaderProps,
  type CustomerGuarnteesProps,
  type VipComponentProps,
  type SingleProductAcceptButtonProps,
  type StandardLayoutProps,
  type SplitLayoutProps,
  type PromoCodeProps,
  type AlwaysVipProps,
  type SavingCalculatorProps,
  type SingleProductProps,
  type DeclineButtonProps,
  type PaymentRadioProps,
  createComponentConfigs,
} from 'shared-components';

type Props = {
  // Layouts
  SplitLayout: SplitLayoutProps;
  StandardLayout: StandardLayoutProps;
  // Blocks
  Columns: ColumnsProps;
  Flex: FlexProps;
  VerticalSpace: VerticalSpaceProps;
  Divider: DividerProps;
  Container: ContainerProps;
  // Text
  TextField: TextFieldProps;
  TextEditor: TextEditorProps;
  // Buttons
  Button: ButtonSectionProps;
  ConfirmPurchase: SingleStepButtonSectionProps;
  // Page Headers
  SimplePageHeader: SimplePageHeaderProps;
  // Form Components
  ContactInfo: ContactInfoProps;
  ShippingForm: MarginProps;
  BillingForm: BillingFormProps;
  ShippingMethodSelect: ShippingMethodSelectProps;
  Payment: PaymentProps;
  // Checkout Components
  Cart: MarginProps;
  Timer: TimerProps;
  TermsAndSub: TermsAndSubProps;
  UpSell: UpSellProps;
  CustomerReviews: CustomerReviewsProps;
  OrderDetails: OrderDetailsProps;
  CustomerGuarantees: CustomerGuarnteesProps;
  ImageUploader: ImageUploaderProps;
  PromoCode: PromoCodeProps;
  VipComponent: VipComponentProps;
  AlwaysVip: AlwaysVipProps;
  SavingCalculator: SavingCalculatorProps;
  PaymentRadio: PaymentRadioProps;
  // upsell
  Header: HeaderProps;
  SingleProduct: SingleProductProps;
  UpsellDeal: SingleProductAcceptButtonProps;
  NextButton: DeclineButtonProps;
};

export const checkoutConfig = (): Config<Props> => {
  const componentList = createComponentConfigs({
    useAppDispatch,
    useAppSelector,
    type: 'all',
  }) as {
    [key in keyof Props]: ComponentConfig<{ props: Props[key] }>;
  };
  return {
    components: componentList,
    root: {
      defaultProps: {
        fontFamily: fontFamilies[0].value,
        themeColor: { r: 79, g: 70, b: 229 },
        errorColor: '#FF5A50',
        customCss: [],
        customScripts: [],
        addProduct: null,
        hideProduct: null,
        asyncLoader: false,
        defer: false,
      },
      render: ({ children, ...props }) => {
        const {
          fontFamily,
          errorColor,
          backgroundColor,
          customCss,
          customScripts,
          themeColor,
          addProduct,
          hideProduct,
          hideProductThankYou,
          customScriptSettings,
        } = props || {};
        return (
          <PuckRenderPage
            fontFamily={fontFamily}
            backgroundColor={backgroundColor}
            errorColor={errorColor}
            customCss={customCss}
            customScripts={customScripts}
            useAppSelector={useAppSelector}
            themeColor={themeColor}
            useAppDispatch={useAppDispatch}
            addProduct={addProduct}
            hideProduct={hideProduct}
            asyncLoader={customScriptSettings?.asyncLoader}
            defer={customScriptSettings?.defer}
            hideProductThankYou={hideProductThankYou}>
            {children}
          </PuckRenderPage>
        );
      },
    },
  };
};

export default checkoutConfig;
